/* istanbul ignore file */
// @flow

// @NOTE(jtan): This module should be used in non-components and as a replacement for injectIntl (to reduce component wrapping)

import { IntlProvider, addLocaleData } from 'react-intl';
import type { IntlShape } from 'react-intl';
import boxLanguages from '@box/languages';

let cachedBoxCldrData;
let cachedIntlProvider;

const createIntlProvider = (locale: string, messages: Object) => {
  return new IntlProvider(
    {
      locale,
      messages,
    },
    {},
  ).getChildContext().intl;
};

const assertIntlProviderExists = () => {
  if (!cachedIntlProvider) {
    throw new Error(
      'i18n Error: User locale is not set properly. This can happen if you call `intl.formatMessage` before the current user is loaded.',
    );
  }
};

// @NOTE(jtan): Written as a normal function to avoid auto-formatting and flow issues with dynamic imports
function importLangBundle(language: string) {
  // $FlowIgnore
  return import(
    /* webpackChunkName: "lang-[request]" */ `lang-bundles/${language}`
  );
}

const loadLanguageBundle = (language: string) => {
  if (!boxLanguages.includes(language)) {
    return Promise.reject(new Error('i18n Error: Unsupported locale.'));
  }

  return importLangBundle(language).then(
    ({ language: locale, messages, reactIntlLocaleData, boxCldrData }) => {
      if (reactIntlLocaleData) {
        addLocaleData(reactIntlLocaleData);
      }
      cachedIntlProvider = createIntlProvider(locale, messages);
      cachedBoxCldrData = boxCldrData;
    },
  );
};

const createIntlProxyFunction = (method: string) => {
  // Retrieves the current intl object
  return (...args: any) => {
    assertIntlProviderExists();
    return cachedIntlProvider[method](...args);
  };
};
type Intl = {
  boxCldrData: any,
  formatDate: $PropertyType<IntlShape, 'formatDate'>,
  formatMessage: $PropertyType<IntlShape, 'formatMessage'>,
  formatNumber: $PropertyType<IntlShape, 'formatNumber'>,
  formatRelative: $PropertyType<IntlShape, 'formatRelative'>,
  formatTime: $PropertyType<IntlShape, 'formatTime'>,
  locale: $PropertyType<IntlShape, 'locale'>,
  messages: $PropertyType<IntlShape, 'messages'>,
};

// We export proxied intl functions so we can use the right locale at runtime
const intl: Intl = {
  formatDate: createIntlProxyFunction('formatDate'),
  formatMessage: createIntlProxyFunction('formatMessage'),
  formatNumber: createIntlProxyFunction('formatNumber'),
  formatRelative: createIntlProxyFunction('formatRelative'),
  formatTime: createIntlProxyFunction('formatTime'),
  get locale() {
    assertIntlProviderExists();
    return cachedIntlProvider.locale;
  },
  get messages() {
    assertIntlProviderExists();
    return cachedIntlProvider.messages;
  },
  get boxCldrData() {
    assertIntlProviderExists();
    return cachedBoxCldrData;
  },
};

export { loadLanguageBundle };
export default intl;
